/**
 * Quiz Game Application - Main App Component
 * 
 * This is the root component of the Quiz Game application that handles routing
 * and provides authentication context to all child components.
 * 
 * Features:
 * - React Router for client-side routing
 * - Authentication context for user management
 * - Error boundaries for fault tolerance
 * - Protected routes for admin functionality
 * 
 * Routes:
 * - /join - Player join page (public)
 * - /game/:gameId - Game room for players (public)
 * - /dashboard - Projector dashboard (public)
 * - /admin - Admin dashboard (protected)
 * - /login - Admin login (public)
 * 
 * @author Quiz Game Team
 * @version 1.0.0
 * @since 2024
 */

import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import ErrorBoundary from './components/ErrorBoundary';
import './App.css';

// Lazy load page components for code splitting and improved TTI
const Login = lazy(() => import('./pages/Login'));
const PlayerJoin = lazy(() => import('./pages/PlayerJoin'));
const GameRoom = lazy(() => import('./pages/GameRoom'));
const GameDashboard = lazy(() => import('./pages/GameDashboard'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));

// Lazy load protected route - only needed for admin routes
const ProtectedRoute = lazy(() => import('./components/ProtectedRoute'));

// Loading component for Suspense fallback
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="text-center">
      <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
      <p className="text-gray-600">Loading...</p>
    </div>
  </div>
);

/**
 * Main App Component
 * 
 * Renders the complete Quiz Game application with routing and authentication.
 * Each route is wrapped in an ErrorBoundary for fault tolerance.
 * 
 * @returns {JSX.Element} The complete application with routing
 */
function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <Router>
          <div className="min-h-screen bg-gray-50">
            <Suspense fallback={<PageLoader />}>
              <Routes>
                {/* Public routes - accessible without authentication */}
                <Route path="/join" element={<ErrorBoundary><PlayerJoin /></ErrorBoundary>} />
                <Route path="/game/:gameId" element={<ErrorBoundary><GameRoom /></ErrorBoundary>} />
                <Route path="/dashboard" element={<ErrorBoundary><GameDashboard /></ErrorBoundary>} />
                <Route path="/login" element={<ErrorBoundary><Login /></ErrorBoundary>} />
                
                {/* Protected admin routes - requires authentication */}
                <Route path="/admin" element={
                  <ErrorBoundary>
                    <Suspense fallback={<PageLoader />}>
                      <ProtectedRoute adminOnly>
                        <AdminDashboard />
                      </ProtectedRoute>
                    </Suspense>
                  </ErrorBoundary>
                } />
                
                {/* Default redirect - redirects to join page */}
                <Route path="/" element={<Navigate to="/join" replace />} />
              </Routes>
            </Suspense>
          </div>
        </Router>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
